import {
  collection,
  doc,
  getDocs,
  getDoc,
  addDoc,
  updateDoc,
  deleteDoc,
  query,
  where,
  orderBy,
  limit,
  onSnapshot,
  Timestamp
} from 'firebase/firestore';
import { db } from './config';

// Types
export interface CarListing {
  id: string;
  title: string;
  make: string;
  model: string;
  year: number;
  price: number;
  currency: string;
  location: string;
  mileage: number;
  fuelType: string;
  transmission: string;
  description: string;
  photos: string[];
  ownerId: string;
  isAvailable: boolean;
  createdAt: Date;
  updatedAt: Date;
}

export interface Event {
  id: string;
  title: string;
  description: string;
  date: Date;
  time: string;
  location: string;
  maxAttendees: number;
  currentAttendees: string[];
  imageUrl: string;
  type: 'upcoming' | 'past';
  createdAt: Date;
  updatedAt: Date;
}

export interface Shop {
  id: string;
  name: string;
  description: string;
  location: string;
  address: string;
  phone: string;
  email: string;
  rating: number;
  reviewCount: number;
  services: string[];
  hours: {
    [key: string]: { open: string; close: string; closed: boolean };
  };
  imageUrl: string;
  badge: 'premium' | 'verified' | 'new';
  isActive: boolean;
  createdAt: Date;
  updatedAt: Date;
}

// Cars Collection
export const carsCollection = collection(db, 'cars');

export const getCars = async (): Promise<CarListing[]> => {
  try {
    const q = query(carsCollection, where('isAvailable', '==', true), orderBy('createdAt', 'desc'));
    const querySnapshot = await getDocs(q);
    return querySnapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data(),
      createdAt: doc.data().createdAt?.toDate() || new Date(),
      updatedAt: doc.data().updatedAt?.toDate() || new Date()
    })) as CarListing[];
  } catch (error) {
    console.error('Error getting cars:', error);
    return [];
  }
};

export const getCarById = async (carId: string): Promise<CarListing | null> => {
  try {
    const carDoc = await getDoc(doc(carsCollection, carId));
    if (!carDoc.exists()) return null;
    
    return {
      id: carDoc.id,
      ...carDoc.data(),
      createdAt: carDoc.data()?.createdAt?.toDate() || new Date(),
      updatedAt: carDoc.data()?.updatedAt?.toDate() || new Date()
    } as CarListing;
  } catch (error) {
    console.error('Error getting car:', error);
    return null;
  }
};

export const addCar = async (carData: Omit<CarListing, 'id' | 'createdAt' | 'updatedAt'>): Promise<string> => {
  try {
    const docRef = await addDoc(carsCollection, {
      ...carData,
      createdAt: Timestamp.now(),
      updatedAt: Timestamp.now()
    });
    return docRef.id;
  } catch (error) {
    console.error('Error adding car:', error);
    throw error;
  }
};

export const updateCar = async (carId: string, carData: Partial<CarListing>): Promise<void> => {
  try {
    const carRef = doc(carsCollection, carId);
    await updateDoc(carRef, {
      ...carData,
      updatedAt: Timestamp.now()
    });
  } catch (error) {
    console.error('Error updating car:', error);
    throw error;
  }
};

export const deleteCar = async (carId: string): Promise<void> => {
  try {
    const carRef = doc(carsCollection, carId);
    await deleteDoc(carRef);
  } catch (error) {
    console.error('Error deleting car:', error);
    throw error;
  }
};

// Events Collection
export const eventsCollection = collection(db, 'events');

export const getUpcomingEvents = async (): Promise<Event[]> => {
  try {
    const q = query(
      eventsCollection,
      where('type', '==', 'upcoming'),
      orderBy('date', 'asc'),
      limit(10)
    );
    const querySnapshot = await getDocs(q);
    return querySnapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data(),
      date: doc.data().date?.toDate() || new Date(),
      createdAt: doc.data().createdAt?.toDate() || new Date(),
      updatedAt: doc.data().updatedAt?.toDate() || new Date()
    })) as Event[];
  } catch (error) {
    console.error('Error getting upcoming events:', error);
    return [];
  }
};

export const getPastEvents = async (): Promise<Event[]> => {
  try {
    const q = query(
      eventsCollection,
      where('type', '==', 'past'),
      orderBy('date', 'desc'),
      limit(10)
    );
    const querySnapshot = await getDocs(q);
    return querySnapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data(),
      date: doc.data().date?.toDate() || new Date(),
      createdAt: doc.data().createdAt?.toDate() || new Date(),
      updatedAt: doc.data().updatedAt?.toDate() || new Date()
    })) as Event[];
  } catch (error) {
    console.error('Error getting past events:', error);
    return [];
  }
};

export const getEventById = async (eventId: string): Promise<Event | null> => {
  try {
    const eventDoc = await getDoc(doc(eventsCollection, eventId));
    if (!eventDoc.exists()) return null;
    
    return {
      id: eventDoc.id,
      ...eventDoc.data(),
      date: eventDoc.data()?.date?.toDate() || new Date(),
      createdAt: eventDoc.data()?.createdAt?.toDate() || new Date(),
      updatedAt: eventDoc.data()?.updatedAt?.toDate() || new Date()
    } as Event;
  } catch (error) {
    console.error('Error getting event:', error);
    return null;
  }
};

export const addEvent = async (eventData: Omit<Event, 'id' | 'createdAt' | 'updatedAt'>): Promise<string> => {
  try {
    const docRef = await addDoc(eventsCollection, {
      ...eventData,
      createdAt: Timestamp.now(),
      updatedAt: Timestamp.now()
    });
    return docRef.id;
  } catch (error) {
    console.error('Error adding event:', error);
    throw error;
  }
};

export const updateEvent = async (eventId: string, eventData: Partial<Event>): Promise<void> => {
  try {
    const eventRef = doc(eventsCollection, eventId);
    await updateDoc(eventRef, {
      ...eventData,
      updatedAt: Timestamp.now()
    });
  } catch (error) {
    console.error('Error updating event:', error);
    throw error;
  }
};

export const deleteEvent = async (eventId: string): Promise<void> => {
  try {
    const eventRef = doc(eventsCollection, eventId);
    await deleteDoc(eventRef);
  } catch (error) {
    console.error('Error deleting event:', error);
    throw error;
  }
};

// Shops Collection
export const shopsCollection = collection(db, 'shops');

export const getShops = async (): Promise<Shop[]> => {
  try {
    const q = query(
      shopsCollection,
      where('isActive', '==', true),
      orderBy('rating', 'desc')
    );
    const querySnapshot = await getDocs(q);
    return querySnapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data(),
      createdAt: doc.data().createdAt?.toDate() || new Date(),
      updatedAt: doc.data().updatedAt?.toDate() || new Date()
    })) as Shop[];
  } catch (error) {
    console.error('Error getting shops:', error);
    return [];
  }
};

export const getShopById = async (shopId: string): Promise<Shop | null> => {
  try {
    const shopDoc = await getDoc(doc(shopsCollection, shopId));
    if (!shopDoc.exists()) return null;
    
    return {
      id: shopDoc.id,
      ...shopDoc.data(),
      createdAt: shopDoc.data()?.createdAt?.toDate() || new Date(),
      updatedAt: shopDoc.data()?.updatedAt?.toDate() || new Date()
    } as Shop;
  } catch (error) {
    console.error('Error getting shop:', error);
    return null;
  }
};

export const addShop = async (shopData: Omit<Shop, 'id' | 'createdAt' | 'updatedAt'>): Promise<string> => {
  try {
    const docRef = await addDoc(shopsCollection, {
      ...shopData,
      createdAt: Timestamp.now(),
      updatedAt: Timestamp.now()
    });
    return docRef.id;
  } catch (error) {
    console.error('Error adding shop:', error);
    throw error;
  }
};

export const updateShop = async (shopId: string, shopData: Partial<Shop>): Promise<void> => {
  try {
    const shopRef = doc(shopsCollection, shopId);
    await updateDoc(shopRef, {
      ...shopData,
      updatedAt: Timestamp.now()
    });
  } catch (error) {
    console.error('Error updating shop:', error);
    throw error;
  }
};

export const deleteShop = async (shopId: string): Promise<void> => {
  try {
    const shopRef = doc(shopsCollection, shopId);
    await deleteDoc(shopRef);
  } catch (error) {
    console.error('Error deleting shop:', error);
    throw error;
  }
};

// Real-time listeners
export const subscribeToCars = (callback: (cars: CarListing[]) => void) => {
  const q = query(carsCollection, where('isAvailable', '==', true));
  return onSnapshot(q, (querySnapshot) => {
    const cars = querySnapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data(),
      createdAt: doc.data().createdAt?.toDate() || new Date(),
      updatedAt: doc.data().updatedAt?.toDate() || new Date()
    })) as CarListing[];
    // Sort by createdAt in JavaScript since we can't use orderBy in the query
    cars.sort((a, b) => b.createdAt.getTime() - a.createdAt.getTime());
    callback(cars);
  });
};

export const subscribeToEvents = (callback: (events: Event[]) => void) => {
  const q = query(eventsCollection);
  return onSnapshot(q, (querySnapshot) => {
    const events = querySnapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data(),
      date: doc.data().date?.toDate() || new Date(),
      createdAt: doc.data().createdAt?.toDate() || new Date(),
      updatedAt: doc.data().updatedAt?.toDate() || new Date()
    })) as Event[];
    // Sort by date in JavaScript since we can't use orderBy in the query
    events.sort((a, b) => a.date.getTime() - b.date.getTime());
    callback(events);
  });
};

export const subscribeToShops = (callback: (shops: Shop[]) => void) => {
  const q = query(shopsCollection, where('isActive', '==', true));
  return onSnapshot(q, (querySnapshot) => {
    const shops = querySnapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data(),
      createdAt: doc.data().createdAt?.toDate() || new Date(),
      updatedAt: doc.data().updatedAt?.toDate() || new Date()
    })) as Shop[];
    // Sort by rating in JavaScript since we can't use orderBy in the query
    shops.sort((a, b) => b.rating - a.rating);
    callback(shops);
  });
};

